import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { t } from '@lingui/macro'
import { isNil } from 'ramda'
import { useCallback, useEffect, useState } from 'react'
import { useTheme } from 'styled-components'

import { Flex, FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { H2 } from '@/@bothrs-ui/web/components/typography/Heading'
import { SignableDocumentReviewCard } from '../../components/signable-document-review-card/SignableDocumentReviewCard'
import { useDocumentActivities } from '../../hooks/useDocumentActivities'
import { useSignableDocuments } from '../../hooks/useSignableDocuments'
import { PdfPreview } from '../pdf-preview/PdfPreview'
import { StButtonContainer, StContainer } from './SignableFormReview.styled'

import type { SignableDocument, SignableDocumentActivity } from '../../types/documents.types'

type Props = {
  details: SignableDocument
  onSigned: () => void
  onAcceptDocument: () => void
}

export const SignableFormActivityReview = ({ details, onSigned, onAcceptDocument }: Props) => {
  const theme = useTheme()

  const {
    getSignableDocumentActivityPreview: {
      execute: getPreview,
      value: preview,
      isLoading: isPreviewLoading,
      reset: resetPreview,
    },
    updateSignableDocumentActivity: { execute: updateDocumentActivity },
    signDocument: { execute: signDocuments, isLoading: isSigningDocuments },
  } = useSignableDocuments()

  const documentActivities = useDocumentActivities(details)

  const unreviewedDocuments = documentActivities?.filter(
    (activity: SignableDocumentActivity) => activity.status !== 'reviewed' && activity.status !== 'signed',
  )

  const [selectedDocumentActivity, setSelectedDocumentActivity] = useState<SignableDocumentActivity>()

  // Functions
  // ------------------------------------------------------------------------- /
  const openDocument = useCallback((activity: SignableDocumentActivity) => {
    setSelectedDocumentActivity(activity)
    getPreview(activity?.id)
  }, [])

  const closeDocument = () => {
    setSelectedDocumentActivity(undefined)
    resetPreview()
  }

  const acceptSelectedDocument = async () => {
    if (!selectedDocumentActivity) {
      return
    }

    try {
      await updateDocumentActivity(selectedDocumentActivity.id, {
        status: 'reviewed',
      })
      setSelectedDocumentActivity(undefined)
      resetPreview()
      onAcceptDocument()

      if (unreviewedDocuments.length === 1) {
        signAllDocuments()
      }
    } catch {
      console.error('error')
    }
  }

  const signAllDocuments = async () => {
    if (!details?.id) {
      return
    }

    try {
      await signDocuments(details.id)
      if (onSigned) {
        onSigned()
      }
    } catch {
      console.error('error')
    }
  }

  // Automatically open document is there is only one
  // ------------------------------------------------------------------------- /
  useEffect(() => {
    if (documentActivities.length === 1 && unreviewedDocuments.length === 1 && isNil(selectedDocumentActivity)) {
      setSelectedDocumentActivity(documentActivities[0])
      openDocument(documentActivities[0])
    }
  }, [])

  // Render
  // ------------------------------------------------------------------------- /
  return (
    <Flex justifyContent='center'>
      <StContainer gap={theme.UI.Spacing.Space8}>
        <FlexColumn gap={theme.UI.Spacing.Space3}>
          <H2 noMargin>{t({ message: 'Controleer en onderteken' })}</H2>
          <Span type='BodyMediumRegular' color={theme.Colors.Neutrals.Darker}>
            {t({
              message: 'Controleer de volgende documenten om je opnameverklaring te kunnen ondertekenen.',
            })}
          </Span>
        </FlexColumn>

        <FlexColumn gap={theme.UI.Spacing.Space3}>
          <Span type='BodyMediumBold'>{t({ message: 'Te controleren' })}</Span>
          {documentActivities.map((activity: SignableDocumentActivity) => (
            <SignableDocumentReviewCard
              key={activity?.id}
              title={activity?.definition?.display}
              icon={['fal', 'file']}
              description={
                activity?.status === 'reviewed'
                  ? ''
                  : t({
                      message: 'Bekijk dit document',
                    })
              }
              reviewed={activity?.status === 'reviewed'}
              onClick={() => {
                openDocument(activity)
              }}
            />
          ))}
        </FlexColumn>
        <StButtonContainer>
          {unreviewedDocuments.length > 0 ? (
            <Button variant='Primary' onClick={() => openDocument(unreviewedDocuments[0])}>
              <Flex justifyContent='center' alignItems='center' gap={theme.UI.Spacing.Space3}>
                {t({
                  message: 'Bekijk document',
                })}
              </Flex>
            </Button>
          ) : (
            <Button variant='Primary' onClick={signAllDocuments} isLoading={isSigningDocuments}>
              <Flex justifyContent='center' alignItems='center' gap={theme.UI.Spacing.Space3}>
                <FontAwesomeIcon icon={['fal', 'signature']} />
                {t({
                  message: 'Onderteken documenten',
                })}
              </Flex>
            </Button>
          )}
        </StButtonContainer>

        {selectedDocumentActivity ? (
          <PdfPreview
            moreInfoButton
            preview={preview}
            filename={selectedDocumentActivity?.definition?.display}
            loading={isPreviewLoading}
            onClose={closeDocument}
            onAccept={acceptSelectedDocument}
            finalDocument={unreviewedDocuments?.length === 1}
          />
        ) : null}
      </StContainer>
    </Flex>
  )
}
